import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { LessonEditLessonSlot } from "@shared/models/analytics/lesson"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { Course, CourseDateAnnotation } from "@shared/models/course"
import { findCourseDatesOff } from "cc-frontend/lib/dates-off-calculator"
import Patch from "@shared/models/patch-updater"
import { getCardStackIdForDate } from "cc-frontend/lib/unit-utils"
import type { PreparedAction } from "@shared/actions/action-doc-builder"
import { assign, get, partial, reject } from "lodash-es"
import moment from "moment"
import type { Narrative } from "@shared/actions/create-narrative"
import createNarrative from "@shared/actions/create-narrative"
import defaultCourseDateActivity from "./default-course-date-activity"
import defaultCourseDateContext from "./default-course-date-context"
import defaultCourseDateModels from "./default-course-date-models"

interface Payload {
  annotation: CourseDateAnnotation
  course: Course
  calledFrom: string
  rotationTitle?: string
}

export default function courseDateAnnotationDeleteAction(payload: Payload): PreparedAction {
  return {
    name: "COURSE_DATE_ANNOTATION_DELETE",
    payload: payload,
    patches: patches(payload),
    undoPatches: undoPatches(payload),
    narrative: partial(narrative, payload),
  }
}

function patches(payload: Payload) {
  let newAnnotations = reject(payload.course.attributes.calendar.dateAnnotations, (annotation) => {
    return annotation.id === payload.annotation.id
  })
  let allDatesOff = findCourseDatesOff(newAnnotations)

  let patch = Patch.create("course", payload.course.id)
    .pull("attributes.calendar.dateAnnotations", {
      id: payload.annotation.id,
    })
    .set("attributes.calendar.datesOff", allDatesOff)
    .inc("attributes.calendar.version", 1)

  return [patch.patch]
}

function undoPatches(payload: Payload) {
  let oldAnnotations = JSON.parse(
    JSON.stringify(payload.course.attributes.calendar.dateAnnotations)
  )
  let allDatesOff = findCourseDatesOff(oldAnnotations)

  let patch = Patch.create("course", payload.course.id)
    .set("attributes.calendar.dateAnnotations", oldAnnotations)
    .set("attributes.calendar.datesOff", allDatesOff)
    .inc("attributes.calendar.version", 1)

  return [patch.patch]
}

function narrative(payload: Payload, findFn: FindFn, userId: string): Narrative {
  let models = defaultCourseDateModels(payload, findFn, userId)

  let datePlanScheduledFor =
    payload.annotation.type === "date-annotation-course-custom-single"
      ? payload.annotation.attributes.dateString
      : null
  let formattedDateString =
    payload.annotation.type === "date-annotation-course-default"
      ? `every ${payload.rotationTitle}`
      : moment(payload.annotation.attributes.dateString).format("ddd, MMM. D, YYYY")

  let cardStackId =
    payload.annotation.type === "date-annotation-course-default"
      ? null
      : getCardStackIdForDate(
          payload.course.attributes.calendar.dates,
          payload.annotation.attributes.dateString
        )

  let courseTitle = payload.course.attributes.title
  let context = defaultCourseDateContext(payload, models)
  let newContext = assign({}, context, {
    noteTitle: get(payload.annotation.attributes, "title"),
    type: payload.annotation.type,
  })

  let newEvent: LessonEditLessonSlot = {
    ...createBlankEvent(),
    root_id: models.planbook.id,
    root_type: "planbook",
    date_plan_scheduled_for: datePlanScheduledFor,
    primary_object_id: cardStackId,
    primary_object_id_and_ancestor_ids: [models.planbook.id, payload.course.id],
    primary_verb: "edit",
    primary_possessive: getPossessiveForPlanbook(findFn, userId, models.planbook.id),
    primary_object_type: "lesson",
    primary_object_specifier: "lesson_slot",

    adverbial_verb: "toggling",
    adverbial_object_type: "lesson",
    adverbial_object_specifier: "on",
    adverbial_object_id: cardStackId,
    adverbial_possessive: null,
    adverbial_object_id_and_ancestor_ids: [models.planbook.id, payload.course.id],
  }

  return createNarrative({
    title: "Turned class back on",
    titleForAnalytics: "Lesson - Toggle On",
    description: `Turned your ${courseTitle} class back on ${formattedDateString}`,
    context: newContext,
    activity: defaultCourseDateActivity(payload, models, "removed"),
    newAnalyticsEvents: [newEvent],
  })
}
